/**
 * RealmsChapter — "Chapter the Second: Of Those Who Wake" (#3305; second since #3723).
 *
 * Replaces the reference mockup's five static Beginnings with live
 * StartingArea rows (one per realm), accented in that realm's own palette
 * via a nested `data-realm` wrapper. Rows expand in place (accordion, one
 * open at a time) to that realm's Beginnings list.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Skeleton } from '@/components/ui/skeleton';
import { usePublicBeginnings, usePublicStartingAreas } from './queries';

function BeginningsList({ startingAreaId }: { startingAreaId: number }) {
  const { data: beginnings, isLoading } = usePublicBeginnings(startingAreaId);

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    );
  }

  if (!beginnings || beginnings.length === 0) {
    return null;
  }

  return (
    <div>
      {beginnings.map((beginning) => (
        <article key={beginning.id} className="gatefold-begin-item">
          <h4>{beginning.name}</h4>
          <p>{beginning.description}</p>
        </article>
      ))}
    </div>
  );
}

export function RealmsChapter() {
  const { data: startingAreas, isLoading } = usePublicStartingAreas();
  const [openAreaId, setOpenAreaId] = useState<string | undefined>(undefined);

  const renderStartingAreas = () => {
    if (isLoading) {
      return (
        <div className="mt-8 space-y-4">
          <Skeleton className="h-6 w-1/3" />
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-6 w-2/5" />
        </div>
      );
    }
    if (startingAreas && startingAreas.length > 0) {
      return (
        <Accordion
          type="single"
          collapsible
          className="mt-8"
          value={openAreaId}
          onValueChange={setOpenAreaId}
        >
          {startingAreas.map((area) => (
            <AccordionItem
              key={area.id}
              value={String(area.id)}
              data-realm={area.realm_theme}
              className="gatefold-realm-item"
            >
              <AccordionTrigger className="gatefold-realm-trigger">
                <span className="gatefold-realm-name" style={{ color: 'hsl(var(--primary))' }}>
                  {area.name}
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <p className="gatefold-realm-desc">{area.description}</p>
                <BeginningsList startingAreaId={area.id} />
                {area.realm_slug && (
                  <p className="gatefold-more-line">
                    <Link to={`/realms/${area.realm_slug}`}>
                      Read the testament of {area.realm_name ?? area.name}{' '}
                      <span aria-hidden="true">→</span>
                    </Link>
                  </p>
                )}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      );
    }
    return null;
  };

  return (
    <div className="gatefold-leaf" id="beginnings">
      <div className="gatefold-leaf-main">
        <span className="gatefold-chapter-no">Chapter the Second</span>
        <h2>Of Those Who Wake</h2>
        <div className="gatefold-leaf-body">
          {/* Apostate's prose, verbatim (#3723). */}
          <p>
            Each of the following starting cities has different Beginnings: Walks of life that have
            shaped the character and their experiences.
          </p>
        </div>
        {renderStartingAreas()}
        <p className="gatefold-more-line">
          <Link to="/roster">
            Browse every Beginning and the character roster <span aria-hidden="true">→</span>
          </Link>
        </p>
      </div>
      <aside>
        {/* Apostate's prose, verbatim (#3723); the Roster word links where it leads. */}
        <span className="gatefold-note">
          <b>Two ways to play:</b> Create your own character or take a pre-made character from our
          available <Link to="/roster">Roster</Link>, each with their own stories and connections.
        </span>
      </aside>
    </div>
  );
}
